import { Badge, Button, Card, IconTile } from "@design";
import {
  ArrowRight,
  Bot,
  ChevronRight,
  Database,
  History,
  LayoutDashboard,
  Palette,
  Plug,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { Text } from "@/components/Text";
import { buildHost } from "@/features/chat/chatHost";

interface Section {
  key: string;
  to: string;
  icon: ReactNode;
  title: string;
  subtitle: string;
}

/**
 * Build home: a featured "Build with AI" hero on top, then a directory of the
 * entity types you can create here (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §2). The
 * hero is the screen's one featured surface (brand tile + badge + arrow); the
 * directory below stays monochrome so it recedes.
 */
export function BuildHomePage() {
  const { t } = useTranslation([
    "build",
    "agents",
    "skills",
    "connectors",
    "dataSources",
    "brands",
    "workflows",
    "microApps",
    "shell",
  ]);
  const navigate = useNavigate();
  const sections: Section[] = [
    {
      key: "agents",
      to: "/build/agents",
      icon: <Bot size={18} />,
      title: t("agents:title"),
      subtitle: t("agents:subtitle"),
    },
    {
      key: "skills",
      to: "/build/skills",
      icon: <Zap size={18} />,
      title: t("skills:title"),
      subtitle: t("skills:subtitle"),
    },
    {
      key: "connectors",
      to: "/build/connectors",
      icon: <Plug size={18} />,
      title: t("connectors:title"),
      subtitle: t("connectors:subtitle"),
    },
    {
      key: "dataSources",
      to: "/build/data-sources",
      icon: <Database size={18} />,
      title: t("dataSources:title"),
      subtitle: t("dataSources:subtitle"),
    },
    {
      key: "brands",
      to: "/build/brands",
      icon: <Palette size={18} />,
      title: t("brands:title"),
      subtitle: t("brands:subtitle"),
    },
    {
      key: "workflows",
      to: "/build/workflows",
      icon: <Workflow size={18} />,
      title: t("workflows:title"),
      subtitle: t("workflows:subtitle"),
    },
    {
      key: "microApps",
      to: "/build/apps",
      icon: <LayoutDashboard size={18} />,
      title: t("microApps:title"),
      subtitle: t("microApps:subtitle"),
    },
  ];

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6 md:gap-8 md:px-8 md:py-10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Text variant="h2" as="h1">
            {t("shell:routes.build")}
          </Text>
          <Text variant="body-sm" tone="secondary" className="mt-1">
            {t("build:hub.subtitle")}
          </Text>
        </div>
        <Button variant="secondary" leadingIcon={<History size={16} />} onClick={() => navigate(buildHost.historyPath)}>
          {t("build:withAi.history")}
        </Button>
      </div>

      {/* The featured action: build anything by describing it. */}
      <Link to="/build/with-ai" className="block">
        <Card interactive size="md" className="border-brand-primary/40">
          <div className="flex items-center gap-4">
            <IconTile variant="brand">
              <Sparkles size={18} aria-hidden />
            </IconTile>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="flex items-center gap-2">
                <Text variant="body">{t("build:withAi.title")}</Text>
                <Badge variant="brand">{t("build:withAi.badge")}</Badge>
              </div>
              <Text variant="body-sm" tone="secondary">
                {t("build:withAi.body")}
              </Text>
            </div>
            <span className="flex shrink-0 items-center gap-1.5 text-brand-primary">
              <Text variant="body-sm" className="text-brand-primary">
                {t("build:withAi.title")}
              </Text>
              <ArrowRight size={16} aria-hidden />
            </span>
          </div>
        </Card>
      </Link>

      {/* The directory of entity types. */}
      <div className="flex flex-col gap-2">
        {sections.map((section) => (
          <Link key={section.key} to={section.to} className="block">
            <Card interactive>
              <div className="flex items-center gap-3">
                <IconTile variant="neutral">{section.icon}</IconTile>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <Text variant="body">{section.title}</Text>
                  <Text variant="body-sm" tone="secondary" className="truncate">
                    {section.subtitle}
                  </Text>
                </div>
                <span className="flex items-center gap-2 text-content-tertiary">
                  <Text variant="body-sm" tone="tertiary">
                    {t("build:hub.viewAll")}
                  </Text>
                  <ChevronRight size={16} aria-hidden />
                </span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
